import React, { useEffect, useState, useRef, createElement } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
// PDF.js v4+ ships the worker as an ESM file ("pdf.worker.min.mjs").
// Resolve it directly from the npm package on unpkg, which mirrors whatever
// version is actually installed. cdnjs paths/filenames are inconsistent
// across versions and were causing "Failed to fetch dynamically imported
// module" errors on the previous "pdf.worker.min.js" URL.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
interface PDFReaderProps {
  pdfUrl: string;
  title: string;
  onClose: () => void;
}
/**
 * Fullscreen PDF reader rendered with pdfjs-dist into <canvas> elements.
 * Works inline on iOS/Android — no native PDF viewer, no iframe, no download.
 */
export function PDFReader({
  pdfUrl,
  title,
  onClose
}: PDFReaderProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Lock background scroll, intercept hardware/browser back, and ESC.
  useEffect(() => {
    const prevOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    try {
      window.history.pushState({
        pdfOpen: true
      }, '');
    } catch {
      // older browsers — ignore
    }
    const onPopState = () => {
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKey);
      window.document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  // Render the PDF into the container as a stack of canvases.
  useEffect(() => {
    let cancelled = false;
    const renderPdf = async () => {
      try {
        setLoading(true);
        setError('');
        if (!containerRef.current) return;
        containerRef.current.innerHTML = '';
        const pdf = await pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false
        }).promise;
        const isMobile = window.innerWidth < 768;
        // Lower base scale on mobile so the page fits the screen width.
        const baseScale = isMobile ? 1.05 : 1.6;
        // Upscale the backing canvas on mobile for retina crispness.
        const backingMultiplier = isMobile ? 1.5 : 1;
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({
            scale: baseScale
          });
          const canvas = window.document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;
          // Backing resolution (higher on mobile).
          canvas.width = Math.floor(viewport.width * backingMultiplier);
          canvas.height = Math.floor(viewport.height * backingMultiplier);
          // Responsive CSS sizing — never wider than the viewport.
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.display = 'block';
          canvas.style.maxWidth = `${viewport.width}px`;
          canvas.style.margin = '0 auto 24px auto';
          canvas.style.borderRadius = '18px';
          canvas.style.background = '#ffffff';
          canvas.style.boxShadow = '0 20px 60px rgba(0,0,0,0.55)';
          canvas.className = 'reader-page';
          containerRef.current?.appendChild(canvas);
          const transform = backingMultiplier !== 1 ? [backingMultiplier, 0, 0, backingMultiplier, 0, 0] : undefined;
          await page.render({
            canvasContext: context,
            viewport,
            transform
          }).promise;
        }
        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error('PDF render error:', err);
        if (!cancelled) {
          setError('No pudimos cargar este documento.');
          setLoading(false);
        }
      }
    };
    renderPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);
  const preventContext = (e: React.MouseEvent | React.SyntheticEvent) => {
    e.preventDefault();
  };
  return <div className="reader-container fixed inset-0 z-[9999] bg-black overflow-y-auto overflow-x-hidden px-2 md:px-8 py-16 md:py-20" onContextMenu={preventContext} data-id="element-529">
      {/* Floating back button — desktop top-left, mobile bottom-center */}
      <button onClick={onClose} aria-label="Volver a los libros" className="fixed z-[10000] rounded-full bg-black/90 border border-[#d4af37]/40 text-[#d4af37] px-5 py-3 text-sm font-semibold tracking-[0.1em] uppercase backdrop-blur-md shadow-lg md:top-4 md:left-4 max-md:bottom-4 max-md:left-1/2 max-md:-translate-x-1/2 max-md:w-[calc(100%-2rem)] max-md:max-w-sm max-md:text-center" style={{
      fontFamily: 'Playfair Display, serif'
    }} data-id="element-530">
        ← Volver a los libros
      </button>

      <div className="fixed top-4 right-4 z-[10000] text-[#d4af37] text-[10px] font-serif uppercase tracking-[0.25em] bg-black/70 border border-[#d4af37]/20 rounded-full px-4 py-2 max-md:hidden" data-id="element-531">
        Lectura protegida
      </div>

      <h2 className="text-center text-white text-xl md:text-4xl font-bold mb-6 md:mb-8 px-4" style={{
      fontFamily: 'Playfair Display, serif'
    }} data-id="element-532">
        {title}
      </h2>

      {loading && <p className="text-center text-[#d4af37] font-serif italic mb-6" data-id="element-533">
          Cargando documento...
        </p>}

      {error && <p className="text-center text-red-400 font-serif italic mb-6" data-id="element-534">
          {error}
        </p>}

      <div ref={containerRef} className="w-full max-w-5xl mx-auto overflow-x-hidden" data-id="element-535" />

      <p className="text-center text-[#d4af37]/70 text-xs font-serif italic mt-8 mb-24 md:mb-16 px-4" data-id="element-536">
        Lectura protegida dentro del Portal de Usuario. Este contenido es
        privado y no debe compartirse.
      </p>
    </div>;
}